import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Logo from "../components/app/Logo";

// Stripe can redirect before its webhook lands. Subscriptions wait for a plan
// change; one-time boosters wait for this user's session to be credited.
export default function BillingSuccess() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const cancelled = useRef(false);
  const query = new URLSearchParams(window.location.search);
  const isBooster = query.get("kind") === "booster";
  const sessionId = query.get("session_id");

  useEffect(() => {
    cancelled.current = false;

    (async () => {
      const MAX_TRIES = 8;
      if (isBooster && !sessionId) {
        toast.error("Missing Booster checkout session.");
        navigate("/settings", { replace: true });
        return;
      }
      for (let i = 0; i < MAX_TRIES; i++) {
        let complete = false;
        let plan = null;
        if (isBooster) {
          try {
            const { data } = await api.get("/billing/booster/status", {
              params: { session_id: sessionId },
            });
            complete = data.applied === true;
          } catch (error) {
            if (error.response?.status === 400 || error.response?.status === 404) {
              toast.error("Could not verify this Booster checkout.");
              navigate("/settings", { replace: true });
              return;
            }
          }
        } else {
          const user = await refresh();
          plan = user?.plan;
          complete = Boolean(plan && plan !== "free");
        }
        if (cancelled.current) return;
        if (complete) {
          setDone(true);
          toast.success(isBooster ? "20 architecture runs added." :
            `You're on the ${plan[0].toUpperCase() + plan.slice(1)} plan.`);
          setTimeout(
            () => !cancelled.current && navigate(isBooster ? "/settings" : "/dashboard", { replace: true }),
            1200
          );
          return;
        }
        await new Promise((r) => setTimeout(r, 1200));
      }
      if (cancelled.current) return;
      toast.message(
        isBooster
          ? "We're still confirming your Booster purchase. Check Settings shortly."
          : "Payment received — your plan is being activated."
      );
      navigate("/settings", { replace: true });
    })();

    return () => {
      cancelled.current = true;
    };
  }, [refresh, navigate, isBooster, sessionId]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-bg-primary text-text-primary"
      data-testid="billing-success"
    >
      <div className="mb-8">
        <Logo />
      </div>
      <div className="flex flex-col items-center gap-4" style={{ maxWidth: 420, textAlign: "center" }}>
        {done ? (
          <CheckCircle2 size={40} color="var(--color-accent)" strokeWidth={2} />
        ) : (
          <Loader2 size={40} className="animate-spin" color="var(--color-accent)" strokeWidth={2} />
        )}
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
          {done ? (isBooster ? "20 runs added" : "You're all set") :
            (isBooster ? "Adding your architecture runs…" : "Activating your subscription…")}
        </h1>
        <p className="text-text-secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>
          {done
            ? `Redirecting you to your ${isBooster ? "settings" : "dashboard"}.`
            : isBooster
              ? "We're confirming your Booster purchase with Stripe."
              : "Thanks for your payment. This only takes a moment while we confirm it with Stripe."}
        </p>
      </div>
    </div>
  );
}
